import React from "react";
import { Table, flexRender } from "@tanstack/react-table";
import { Box, CircularProgress } from "@mui/material";
import fourDotLoader from "assets/icons/FourDotLoader.gif";

const LOADER_PRIMARY = "#1e5db8";

export type DataTableLoaderVariant = "dots";

function FourDotsLoader() {
  return (
    <img
      src={fourDotLoader}
      alt=""
      width={104}
      height={101}
      style={{ display: "block", background: "transparent" }}
    />
  );
}

type DataTableLoadingIndicatorProps = {
  message?: string;
  subtitle?: string;
  minHeight?: number;
  variant?: DataTableLoaderVariant;
};

/** Large centered spinner — easy to see when data is loading. */
export function DataTableLoadingIndicator({
  message = "Loading data…",
  subtitle = "Please wait",
  minHeight = 240,
  variant = "dots",
}: DataTableLoadingIndicatorProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: variant === "dots" ? 0.5 : 1.5,
        py: 4,
        px: 2,
        minHeight,
      }}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {variant === "dots" ? (
        <FourDotsLoader />
      ) : (
        <CircularProgress size={46} thickness={4.5} sx={{ color: LOADER_PRIMARY }} />
      )}
      <Box sx={{ fontWeight: 700, fontSize: "1.05rem", color: LOADER_PRIMARY, letterSpacing: "0.01em" }}>
        {message}
      </Box>
      {subtitle ? (
        <Box sx={{ fontSize: "0.875rem", color: "#64748b" }}>{subtitle}</Box>
      ) : null}
    </Box>
  );
}

type DesktopTableLoaderProps<T> = {
  table: Table<T>;
  message?: string;
  variant?: DataTableLoaderVariant;
};

/** Desktop `<thead>` + clear loading panel in `<tbody>`. */
export function DesktopTableLoader<T>({
  table,
  message = "Loading data…",
  variant = "dots",
}: DesktopTableLoaderProps<T>) {
  const colCount = Math.max(1, table.getVisibleLeafColumns().length);

  return (
    <>
      <thead className="production-thead">
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((h) => (
              <th key={h.id} className="production-th">
                {h.isPlaceholder
                  ? null
                  : flexRender(h.column.columnDef.header, h.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        <tr className="production-tr">
          <td colSpan={colCount} className="production-td p-3" style={{ borderBottom: "none" }}>
            <DataTableLoadingIndicator message={message} minHeight={260} variant={variant} />
          </td>
        </tr>
      </tbody>
    </>
  );
}

type MobileCardsLoaderProps = {
  message?: string;
  variant?: DataTableLoaderVariant;
};

/** Mobile loading panel — same visible spinner as desktop. */
export function MobileCardsLoader({ message = "Loading data…", variant = "dots" }: MobileCardsLoaderProps) {
  return (
    <div className="px-2 py-2">
      <DataTableLoadingIndicator message={message} minHeight={200} variant={variant} />
    </div>
  );
}

type DataTableCenteredLoaderProps = {
  message?: string;
  subtitle?: string;
  minHeight?: number;
};

/** Centered spinner — use inside cards or standalone panels. */
export function DataTableCenteredLoader({
  message = "Loading data…",
  subtitle = "Please wait",
  minHeight = 220,
}: DataTableCenteredLoaderProps) {
  return (
    <DataTableLoadingIndicator
      message={message}
      subtitle={subtitle}
      minHeight={minHeight}
    />
  );
}

type DataTableEmptyStateProps = {
  message?: string;
  className?: string;
};

export function DataTableEmptyState({
  message = "No records found",
  className = "text-center my-4 text-muted",
}: DataTableEmptyStateProps) {
  return (
    <div className={className} style={{ fontSize: "1.1rem" }}>
      {message}
    </div>
  );
}

type DataTableStatusProps = {
  loading: boolean;
  isEmpty: boolean;
  emptyMessage?: string;
  children?: React.ReactNode;
};

/**
 * Renders pagination / footer only when not loading and table has rows.
 * Wrap pagination controls with this helper across datatable pages.
 */
export function DataTableStatus({
  loading,
  isEmpty,
  emptyMessage,
  children,
}: DataTableStatusProps) {
  if (loading) return null;
  if (isEmpty) return <DataTableEmptyState message={emptyMessage} />;
  return <>{children}</>;
}
